import type { ComponentType, ReactNode } from 'react';
import { Type, Static } from '@sinclair/typebox';
import { Value } from '@sinclair/typebox/value';

/** Вес по умолчанию для модулей без navigationOrder — в конец меню. */
export const DEFAULT_NAVIGATION_WEIGHT = 9999999;

/** Множитель для расчёта веса: moduleOrder * WEIGHT_FACTOR + groupOrder */
export const NAVIGATION_WEIGHT_FACTOR = 100;

/**
 * Референс группы навигации. Создаётся через createNavigationGroup() в bootstrap модуля.
 * Содержит метаданные группы для отображения в меню.
 */
export interface NavigationGroupRef {
  id: string;
  label: string;
  icon: ComponentType<{ size?: number; className?: string; stroke?: number }>;
  /** Порядок группы внутри модуля (меньше — выше). */
  order: number;
}

/**
 * Пропсы контейнерного layout-компонента, который оборачивает <Outlet /> в BaseLayout.
 */
export type NavigationLayoutProps = {
  children: ReactNode;
  isFirstActive: boolean;
  activeTabTouchesRight: boolean;
};

/**
 * Компонент контейнерного layout для конкретного route.
 */
export type NavigationLayout = ComponentType<NavigationLayoutProps>;

/**
 * TypeBox-схема для layout.
 * На runtime здесь хранится React-компонент, поэтому используем Type.Any.
 */
export const NavigationLayoutSchema = Type.Any({
  title: 'Container layout',
  description: 'React-компонент контейнера для route внутри BaseLayout',
});

/**
 * NavigationItem - TypeBox schema for sidebar/menu navigation items
 * Used by all modules for consistent navigation structure
 */
export const NavigationItemSchema = Type.Object({
  label: Type.String({
    title: 'Название',
    description: 'Текст для отображения в меню',
    minLength: 1,
  }),
  icon: Type.Any({
    title: 'Иконка',
    description: 'React-иконка для меню',
  }),
  url: Type.String({
    title: 'URL',
    description: 'Путь для перехода',
    minLength: 1,
  }),
  component: Type.Optional(Type.Any({
    title: 'Компонент',
    description: 'React-компонент страницы',
  })),
  hiddenFromMenu: Type.Optional(Type.Boolean({
    title: 'Скрыть из меню',
    default: false,
  })),
  badge: Type.Optional(Type.Any({
    title: 'Бейдж',
    description: 'React-элемент для бейджа',
  })),
  guard: Type.Optional(Type.Any({
    title: 'Guard функция',
    description: 'Функция-предикат для условного отображения пункта меню. Сигнатура: () => boolean. Возвращает true, если пункт должен отображаться, false — если скрыть. Не должна бросать исключения.',
  })),
  layout: Type.Optional(NavigationLayoutSchema),
  /** Референс группы. Создаётся через createNavigationGroup(). */
  group: Type.Optional(Type.Any({
    title: 'Группа',
    description: 'Референс группы навигации из createNavigationGroup()',
  })),
});

/**
 * NavigationItem — элемент навигации для меню.
 * 
 * Поле `guard` — опциональная функция-предикат для условного отображения пункта меню.
 * 
 * **Сигнатура guard:** `() => boolean`
 * - Возвращает `true`, если пункт должен отображаться
 * - Возвращает `false`, если пункт должен быть скрыт
 * - **Не должна бросать исключения** — при ошибке пункт будет скрыт
 * 
 * @example
 * ```ts
 * {
 *   label: 'Пользователи',
 *   url: '/identity/users',
 *   icon: IconUsers,
 *   guard: () => {
 *     const user = AuthController.getInstance().getSnapshot().user;
 *     return user?.role === 'admin';
 *   }
 * }
 * ```
 */
export type NavigationItem = Omit<Static<typeof NavigationItemSchema>, 'layout'> & {
  layout?: NavigationLayout;
};

/**
 * Validates a navigation item against the schema.
 * Returns { valid, errors }.
 */
export function validateNavigationItem(item: unknown): { valid: boolean; errors?: string[] } {
  const errors = [...Value.Errors(NavigationItemSchema, item)].map(e => `${e.path}: ${e.message}`);
  return {
    valid: errors.length === 0,
    errors: errors.length > 0 ? errors : undefined,
  };
} 
